import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from "styled-components";
import Layout from '@shared/ui/organisms/layout/Layout';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { redirectTo, selectCurrentUser } from "@shared/store/auth.duck";
import PlanUsageContentCard from "./PlanUsageContentCard";
import CancelSection from "./CancelSection";
import { Permission } from "@shared/models/user.models";
import AccessRequired from "../components/AccessRequired";
import { loadAccountInfo, selectAccountInfo, selectIsAccountActive } from "@features/settings/store/payments.duck";
import { DialogIdentifiers, openDialog } from '@shared/store/dialog.duck';
import { paymentsEnabled } from "@shared/services/EnvironmentVariables";
import Colours from '@shared/ui/atoms/Colours';
import { AccountSummarySection } from "./AccountSummarySection";
import { UpgradeSection } from "./UpgradeSection";
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { isInTeamsStoreApp } from "@shared/utils/TeamsUtils";
import { useTranslation } from "react-i18next";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { PastInvoicesSection } from './PastInvoicesSection';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { SidebarItem } from '../office-management-page-v2/components/SidebarItem';

enum PaymentsView {
  YourPlan,
  Invoices,
}

export default function PaymentsPage() {
  const dispatch = useDispatch();
  const accountInfo = useSelector(selectAccountInfo);
  const isAccountActive = useSelector(selectIsAccountActive);
  const user = useSelector(selectCurrentUser);
  const isAdmin = user?.role === Permission.CompanyAdmin;
  const { t } = useTranslation();
  const [activePanel, setActivePanel] = useState<PaymentsView>(PaymentsView.YourPlan);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const usesStripe = !!accountInfo?.usesStripe;

  useEffect(() => {
    if (isInTeamsStoreApp()) {
      dispatch(redirectTo('/manage'))
    } else if (isAdmin) {
      dispatch(loadAccountInfo());
    }
  }, [dispatch, isAdmin])

  if (!paymentsEnabled) {
    window.location.href = '/manage/teams-and-users'
  }

  const createNewSubscription = () => {
    dispatch(openDialog(DialogIdentifiers.NewPaymentDialog));
  }

  const menuItems = useMemo(() => [
    { view: PaymentsView.YourPlan, icon: IconTypes.Billing, label: 'payments.nav-your-plan', visible: true },
    { view: PaymentsView.Invoices, icon: IconTypes.Reporting, label: 'payments.nav-invoices', visible: isAdmin && usesStripe },
  ].filter(item => item.visible), [isAdmin, usesStripe]);

  const activeItem = menuItems.find(item => item.view === activePanel) ?? menuItems[0];

  const onPanelChange = (view: PaymentsView) => {
    setActivePanel(view);
    setMobileMenuOpen(false);
  }

  return (
    <Layout fullWidth={true} hideFooter={true}>
      <RibbonContainer>
        <RibbonContentContainer>
          <GoBackButton url={'/manage'} style={{ marginBottom: 0 }} />
        </RibbonContentContainer>
      </RibbonContainer>

      <ContentContainer>
        <Sidebar>
          <MobileMenuToggle onClick={() => setMobileMenuOpen(open => !open)}
                            aria-expanded={mobileMenuOpen}
                            type="button">
            <MobileMenuToggleLabel>
              <Icon icon={activeItem?.icon ?? IconTypes.Billing} />
              <BodyRegular weight={600}>{t(activeItem?.label ?? 'payments.nav-your-plan')}</BodyRegular>
            </MobileMenuToggleLabel>
            <Icon icon={mobileMenuOpen ? IconTypes.Up : IconTypes.Down} />
          </MobileMenuToggle>
          <SidebarItems $open={mobileMenuOpen}>
            {menuItems.map(item => (
              <SidebarItem key={item.view}
                           icon={item.icon}
                           label={item.label}
                           active={activePanel === item.view}
                           onClick={() => onPanelChange(item.view)} />
            ))}
          </SidebarItems>
        </Sidebar>

        <MainContent>
          <AccessRequired visible={!isAdmin} companyAdminsRequired={true} />

          {activePanel === PaymentsView.YourPlan && <>
            <HeadlineMedium style={{ marginBottom: 24 }}>{t('settings.billing-header')}</HeadlineMedium>
            <PlanUsageContentCard />

            {usesStripe ? <>
              {isAccountActive && <AccountSummarySection />}

              {isAdmin && !isAccountActive &&
                <PaymentDetailsContainer>
                  <OutlineButton size={ButtonSize.Large}
                                 click={createNewSubscription}
                                 text="button.create-subscription" />
                </PaymentDetailsContainer>}
            </> : <></>}

            <UpgradeSection />

            <CancelSection />
          </>}

          {activePanel === PaymentsView.Invoices && isAdmin && usesStripe && <PastInvoicesSection />}
        </MainContent>
      </ContentContainer>
    </Layout>
  );
}

const Sidebar = styled.div`
  width: 100%;
  @media (${tablet}) {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    margin: 0;
  }
`

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: white;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  cursor: pointer;
  i {
    margin-right: 10px;
  }
  @media (${tablet}) {
    display: none;
  }
`

const MobileMenuToggleLabel = styled.div`
  display: flex;
  align-items: center;
`

const SidebarItems = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'block' : 'none'};
  padding: 16px 16px 12px 16px;
  @media (${tablet}) {
    display: block;
    padding: 0;
  }
`

const MainContent = styled.div`
  flex: 1;
  min-height: 500px;
  background-color: white;
  padding: 32px 16px 0 16px;
  @media (${tablet}) {
    padding: 48px 120px;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: ${Colours.veryLightGrey};
  @media (${tablet}) {
    padding: 0 16px;
    background-color: unset;
    display: flex;
    margin: 24px auto 0 auto;
  }
`

const RibbonContainer = styled.div`
  background-color: white;
  padding: 0 16px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  padding: 16px 0;
`

const PaymentDetailsContainer = styled.div`
  margin-bottom: 24px;
  padding-bottom: 32px;
  border-bottom: 1px solid ${Colours.mildGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  button {
    max-width: 400px;
    margin-bottom: 16px;
  }
`
